import React, {Fragment} from 'react';
import {Link, Form, Edit, Date, List, Breadcrumbs, Layout, Separator, Activities, Icon, Submit, Button} from 'components';
import {
  useParams,
  useSearchParams,
  useSelector,
  useFormRef,
  returnNull,
  modal,
  redirect,
  useNamedSelector, moment
} from 'helpers';
import {goals} from 'store';
import {Goal} from 'types';
import {GoalsDropdown} from '.';

export function EditForm() {
  const params: Record<string, string> = {
    category: 'other',
    ...useParams(),
    ...useSearchParams(),
  }

  const isCreate = !params.id;
  const form = useFormRef<Partial<Goal>>();
  const {goal, activities, editable} = useSelector((state) => goals.details(state, {id: params.id, context: 'goals'}));

  const { firstDayOfWeek } = useNamedSelector('settings.settings');
  moment.updateLocale('en', {
    week: {
      dow: firstDayOfWeek - 1 // 1 = Sunday, 2 = Monday
    }
  });
  const weekEndMillis = moment().clone().endOf('week').unix();

  const initialValues = {
    parent_id: goal?.parent_id,
    name: goal?.name,
    note: goal?.note,
    target_date: goal ? goal.target_date : weekEndMillis,
    activities: goal ? activities : [],
    ...goal,
  }

  const save = (goal: Partial<Goal>) => {
    goal.isWeeklyObjective = true;
    const {goal: {id}} = goals.dispatch.upsert({
      ...goal,
    });
    redirect(`/goals/weekly-objectives/${id}/`, true);
  }

  const change = (goal: Partial<Goal>) => {
    if (isCreate) return;
    goals.dispatch.upsert({
      ...goal,
    });
  }

  return (
    <Form id="weeklyObjectiveForm" initialValues={initialValues} onSubmit={save} onChange={change} delay innerRef={form}>
      {({values}) => (
        <Fragment>
          {!isCreate
            ?
            <Layout.Header justified>
              <Breadcrumbs>
                <Link to="/goals/weekly-objectives/">Weekly Objectives</Link>
                <b>Edit</b>
              </Breadcrumbs>
            </Layout.Header>
            :
            <Layout.Header justified>
              <Breadcrumbs>
                <Link to="/goals/weekly-objectives/">Weekly Objectives</Link>
                <b>Create</b>
              </Breadcrumbs>
              <Submit className="small" icon={!goal && 'arrow-right'}>
                { goal ? [ 'Save', 'Saving..', 'Saved' ] : ['Next']}
              </Submit>
            </Layout.Header>
          }
          <div>
            <List small-gutter>
              <Edit
                bold
                name="name"
                placeholder="Enter weekly objective name.."
                required
              />
              <Edit
                name="note"
                placeholder="Enter note.."
              />
              <Date
                name="target_date"
                label="Target Date"
                minDate={values.starting_date}
                required
              />
              <GoalsDropdown
                name="parent_id"
              />
            </List>
          </div>
          { !isCreate &&
            <div className="medium-spacing">
              <Separator className="medium" icon="clipboard">
                Activities
                <u />
                { editable && <Icon name="plus" onClick={modal(Activities.Single.EditForm, { goal })} /> }
              </Separator>
              <p>Make a list of everything you have to do to achieve this weekly objective.</p>
              { activities.map(activity =>
                <Activities.Card
                  Checkbox={Activities.Checkbox}
                  Status={returnNull}
                  data-object-type="activity"
                  full
                  id={activity.id}
                  key={activity.id}
                  context={{ goal }}
                />
              ) }
              <p className="hidden-print">
                <Button icon="plus" className={['primary']} onClick={modal(Activities.Single.EditForm, { goal })} >
                  Add Activity
                </Button>
              </p>
            </div>
          }
        </Fragment>
      )}
    </Form>
  );
}
